import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../services/authService';
import { FaChevronDown, FaSignOutAlt, FaUser, FaUsers } from 'react-icons/fa';
import styles from './RightNav.module.css';

function getInitials(user) {
  const source = user?.full_name || user?.name || user?.firstName || user?.email || '';
  if (!source) return '?';
  const parts = source
    .replace(/@.*$/, '')
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (parts.length === 0) return source.charAt(0).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getDisplayName(user) {
  return user?.full_name || user?.name || user?.firstName || user?.email || 'User';
}

export default function RightNav({ user, isAdmin }) {
  const [open, setOpen] = useState(false);
  const navRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function handleEscape(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = getInitials(user);
  const displayName = getDisplayName(user);

  return (
    <div className={styles.container} ref={navRef}>
      <button
        type="button"
        className={[styles.trigger, open ? styles.triggerOpen : ''].join(' ')}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open user menu"
      >
        <span className={styles.avatar} aria-hidden="true">
          {initials}
        </span>
        <span className={styles.name}>{displayName}</span>
        <FaChevronDown className={[styles.chevron, open ? styles.chevronOpen : ''].join(' ')} />
      </button>

      {open && (
        <div className={styles.menu} role="menu">
          <div className={styles.menuUser}>
            <span className={styles.menuAvatar} aria-hidden="true">
              {initials}
            </span>
            <span className={styles.menuUserText}>
              <span className={styles.menuUserName}>{displayName}</span>
              {user?.email ? <span className={styles.menuUserEmail}>{user.email}</span> : null}
              {isAdmin ? <span className={styles.adminBadge}>Admin</span> : null}
            </span>
          </div>

          <div className={styles.menuSection}>
            <button
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={() => {
                setOpen(false);
                navigate('/profile');
              }}
            >
              <FaUser className={styles.menuItemIcon} />
              <span>Profile</span>
            </button>

            {isAdmin && (
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => {
                  setOpen(false);
                  navigate('/users', { replace: true });
                }}
              >
                <FaUsers className={styles.menuItemIcon} />
                <span>Manage users</span>
              </button>
            )}

          </div>

          <div className={styles.menuDivider} />

          <button
            type="button"
            role="menuitem"
            className={[styles.menuItem, styles.logout].join(' ')}
            onClick={handleLogout}
          >
            <FaSignOutAlt className={styles.menuItemIcon} />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}
